import React, { useEffect, useRef } from "react"
import axios from "axios"
import PersonalInfo from "./Sections/personal-info"
import ContactInfo from "./Sections/contact-info"
import VehicleInformation from "./Sections/vehicle-information"
import AccountInfo from "./Sections/account-info"
import AccountPermissions from "./Sections/account-permissions"
import ChangePasswordModal from "./Sections/change-password-modal"
import Spinner from "../../../booking/components/spinner"
import PropTypes from "prop-types"
import useProfileStore from "./profileStore"

EditProfileComponent.propTypes = {
  lang: PropTypes.string.isRequired,
  hosts: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  isGoogleLinked: PropTypes.bool.isRequired,
  isFacebookLinked: PropTypes.bool.isRequired,
  contentLink: PropTypes.number.isRequired,
  marketingConsentCurrent: PropTypes.object.isRequired,
  marketingConsentLatest: PropTypes.object.isRequired,
  parkingConsentCurrent: PropTypes.object.isRequired,
  parkingConsentLatest: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
}

export default function EditProfileComponent({
  lang,
  hosts,
  profile,
  countries,
  isGoogleLinked,
  isFacebookLinked,
  contentLink,
  marketingConsentCurrent,
  marketingConsentLatest,
  parkingConsentCurrent,
  parkingConsentLatest,
  labels,
}) {
  const defaultMobileNumber = "+45"
  const {
    membershipNumber,
    setMembershipNumber,
    fullName,
    setFullName,
    birthDay,
    setBirthDay,
    birthMonth,
    setBirthMonth,
    birthYear,
    setBirthYear,
    gender,
    setGender,
    street,
    setStreet,
    postalCode,
    setPostalCode,
    city,
    setCity,
    email,
    setEmail,
    oldEmail,
    setOldEmail,
    mobileNumber,
    setMobileNumber,
    setCountry,
    countryCode,
    setCountryCode,
    setAcceptedNewsletterConsentVersion,
    setAcceptedParkingServicesConsentVersion,
    vehicleRegistrationNumber,
    setVehicleRegistrationNumber,
    vehicleMake,
    setVehicleMake,
    vehicleModel,
    setVehicleModel,
    vehicleColour,
    setVehicleColour,
    contactPermissionIsChecked,
    setContactPermissionIsChecked,
    setContactPermissionVersion,
    parkingContactPermissionIsChecked,
    setParkingContactPermissionIsChecked,
    setParkingContactPermissionVersion,
    isFullNameValid,
    isEmailValid,
    isMobileValid,
    isCountryValid,
    showUpdatedProfileMessage,
    setShowUpdatedProfileMessage,
    updatedProfileMessage,
    isPasswordModalOpen,
    ajaxSpinnerActive,
    setAjaxSpinnerActive,
    setLoadingLicensePlate,
    contactPermissionVersion,
    parkingContactPermissionVersion,
  } = useProfileStore()

  useEffect(() => {
    setMembershipNumber(profile.MembershipNumber)
    setFullName(profile.Name || "")
    setBirthDay(profile.Day || "")
    setBirthMonth(parseInt(profile.Month, 10) || "")
    setBirthYear(profile.Year || "")
    setGender(profile.Gender || "")
    setStreet(profile.StreetName || "")
    setPostalCode(profile.ZipCode || "")
    setCity(profile.City || "")
    setEmail(profile.Email || "")
    setOldEmail(profile.Email || "")
    setMobileNumber(profile.MobileNumber || defaultMobileNumber)
    setCountryCode(profile.CountryCode || "DK")
    setAcceptedNewsletterConsentVersion(profile.NewsletterVersion)
    setAcceptedParkingServicesConsentVersion(profile.ParkingServicesVersion)
    setVehicleRegistrationNumber(profile.VehicleRegistrationNumber || "")
    setVehicleMake(profile.VehicleMake || "")
    setVehicleModel(profile.VehicleModel || "")
    setVehicleColour(profile.VehicleColour || "")
    setContactPermissionIsChecked(profile.NewsLetter)
    setContactPermissionVersion(profile.NewsLetter ? marketingConsentCurrent.ConsentVersion : marketingConsentLatest.ConsentVersion)
    setParkingContactPermissionIsChecked(profile.ParkingServicesPermission)
    setParkingContactPermissionVersion(profile.ParkingServicesVersion ? parkingConsentCurrent.ConsentVersion : parkingConsentLatest.ConsentVersion)
  }, [])

  const personalInfoRef = useRef(undefined)
  const contactInfoRef = useRef(undefined)
  const editPasswordButtonId = "edit-password"

  const scrollToRef = (reference) => {
    const offset = 150
    const elementPosition = reference.current.getBoundingClientRect().top
    const offsetPosition = elementPosition - offset

    window.scrollBy({
      top: offsetPosition,
      behavior: "smooth",
    })
  }

  //profileData
  const handleUpdateProfile = (e) => {
    e.preventDefault()

    if (!isFullNameValid || !isCountryValid) {
      scrollToRef(personalInfoRef)
      return
    }
    if (!isEmailValid || !isMobileValid) {
      scrollToRef(contactInfoRef)
      return
    }

    setAjaxSpinnerActive(true)

    const newProfile = {
      Name: fullName,
      Gender: gender,
      Day: birthDay,
      Month: birthMonth,
      Year: birthYear,
      ZipCode: postalCode,
      City: city,
      CountryCode: countryCode,
      StreetName: street,
      MembershipNumber: membershipNumber,
      MobileNumber: mobileNumber === defaultMobileNumber ? null : mobileNumber,
      OldEmail: oldEmail,
      Email: email,
      NewsLetter: contactPermissionIsChecked,
      ParkingServicesPermission: parkingContactPermissionIsChecked,
      VehicleRegistrationNumber: vehicleRegistrationNumber,
      VehicleColour: vehicleColour,
      VehicleMake: vehicleMake,
      VehicleModel: vehicleModel,
      WebVistorgroupsJson: profile.WebVistorgroupsJson,
    }

    if (contactPermissionIsChecked) newProfile.NewsletterVersion = contactPermissionVersion
    if (parkingContactPermissionIsChecked) newProfile.ParkingServicesVersion = parkingContactPermissionVersion

    axios
      .post("/IdpImplicitFlow/UpdateProfile", newProfile)
      .then((res) => {
        // Handle the response data (if needed)
        if (res && res.status === 200) {
          if (profile.MobileNumber !== mobileNumber && mobileNumber !== defaultMobileNumber) {
            window.location.assign(window.location + "?verifyPhone=true")
          }
          if (profile.MobileNumber === mobileNumber || (mobileNumber === defaultMobileNumber && !profile.MobileNumber)) {
            setShowUpdatedProfileMessage(true)
          }
        }
      })
      .catch((error) => {
        // Handle errors
        console.error("Error updating profile:", error.message)
      })
      .finally(() => {
        setAjaxSpinnerActive(false)
        setTimeout(() => {
          setShowUpdatedProfileMessage(false)
        }, 5000)
      })
  }
  const findCountryByIsoCode = (isoCode) => {
    const countryObject = countries.find((i) => i.Value === isoCode)
    return countryObject ? countryObject.Text : null
  }

  const handleCancelUpdateProfile = (e) => {
    e.preventDefault()

    setFullName(profile.Name)
    setBirthDay(profile.Day || "")
    setBirthMonth(parseInt(profile.Month, 10) || "")
    setBirthYear(profile.Year || "")
    setGender(profile.Gender || "")
    setStreet(profile.StreetName || "")
    setPostalCode(profile.ZipCode || "")
    setCity(profile.City || "")
    setEmail(profile.Email || "")
    setOldEmail(profile.Email || "")
    setMobileNumber(profile.MobileNumber || "+45")
    setCountryCode(profile.CountryCode || "DK")
    setCountry(findCountryByIsoCode(profile.CountryCode || "DK"))

    setVehicleRegistrationNumber(profile.VehicleRegistrationNumber || "")
    setVehicleMake(profile.VehicleMake || "")
    setVehicleModel(profile.VehicleModel || "")
    setVehicleColour(profile.VehicleColour || "")
    setLoadingLicensePlate(false)
  }

  //#region requests

  //#endregion

  //#endregion

  return (
    <div className="edit-profile-container">
      <form id="formProfile" action="/IdpImplicitFlow/UpdateProfile" className="form form--profile js-profile-page">
        <div className="w--wider">
          <PersonalInfo personalInfoRef={personalInfoRef} isGoogleLinked={isGoogleLinked} labels={labels} lang={lang} countries={countries} />

          <ContactInfo contactInfoRef={contactInfoRef} labels={labels} />

          <VehicleInformation labels={labels} hosts={hosts} profile={profile} />

          <AccountInfo
            editPasswordButtonId={editPasswordButtonId}
            headingAccountInformation={labels.headingAccountInformation}
            labelMembershipNumber={labels.labelMembershipNumber}
            membershipNumber={profile.MembershipNumber}
            labelPassword={labels.labelPassword}
            editPasswordLinkLabel={labels.editPasswordLinkLabel}
          />

          <AccountPermissions
            labels={labels}
            marketingConsentCurrent={marketingConsentCurrent}
            marketingConsentLatest={marketingConsentLatest}
            parkingConsentCurrent={parkingConsentCurrent}
            parkingConsentLatest={parkingConsentLatest}
          />

          <div className="action-buttons">
            <input id="profileData" name="profilData" type="hidden" value=""></input>

            <a className="link" href="" onClick={(e) => handleCancelUpdateProfile(e)}>
              <span>{labels.labelButtonCancel}</span>
            </a>
            <button id="profileSubmit" onClick={(e) => handleUpdateProfile(e)} className="submit button button--medium button--general">
              {labels.labelButtonSave}
            </button>
          </div>
          {showUpdatedProfileMessage ? (
            <p style={{ color: "#269053", textAlign: "right", padding: "15px" }} className="is-success">
              {updatedProfileMessage}
            </p>
          ) : undefined}
        </div>
      </form>

      {isPasswordModalOpen ? <ChangePasswordModal returnFocusId={editPasswordButtonId} labels={labels} /> : undefined}
      <Spinner isActive={ajaxSpinnerActive} isTransparent={false} />
    </div>
  )
}
